import React from 'react';
import { TrendingDown, PieChart, Calculator, LineChart } from 'lucide-react';

const DepreciationMethodSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Метод амортизации и дополнительные параметры
      </h3>
      
      {/* Выбор метода амортизации */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <label className={`
          flex items-start p-3 border rounded-lg cursor-pointer transition-colors
          ${formData.depreciationMethod === 'straight_line' 
            ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-700' 
            : 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600'}
        `}>
          <input
            type="radio"
            name="depreciationMethod"
            value="straight_line"
            checked={formData.depreciationMethod === 'straight_line'}
            onChange={handleInputChange}
            className="form-radio mt-1"
          />
          <div className="ml-3">
            <span className="block text-sm font-medium text-gray-900 dark:text-white flex items-center">
              <LineChart className="w-4 h-4 mr-1.5 text-indigo-500" />
              Линейный метод
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
              Равномерное списание стоимости актива в течение срока использования
            </span>
          </div>
        </label>
        
        <label className={`
          flex items-start p-3 border rounded-lg cursor-pointer transition-colors
          ${formData.depreciationMethod === 'reducing_balance' 
            ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-700' 
            : 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600'}
        `}>
          <input
            type="radio"
            name="depreciationMethod"
            value="reducing_balance"
            checked={formData.depreciationMethod === 'reducing_balance'}
            onChange={handleInputChange}
            className="form-radio mt-1"
          />
          <div className="ml-3">
            <span className="block text-sm font-medium text-gray-900 dark:text-white flex items-center">
              <TrendingDown className="w-4 h-4 mr-1.5 text-indigo-500" />
              Метод уменьшаемого остатка
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
              Ускоренное списание в первые годы с постепенным уменьшением суммы
            </span>
          </div>
        </label>
        
        <label className={`
          flex items-start p-3 border rounded-lg cursor-pointer transition-colors
          ${formData.depreciationMethod === 'sum_of_years' 
            ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-700' 
            : 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600'}
        `}>
          <input
            type="radio"
            name="depreciationMethod"
            value="sum_of_years"
            checked={formData.depreciationMethod === 'sum_of_years'}
            onChange={handleInputChange}
            className="form-radio mt-1"
          />
          <div className="ml-3">
            <span className="block text-sm font-medium text-gray-900 dark:text-white flex items-center">
              <PieChart className="w-4 h-4 mr-1.5 text-indigo-500" />
              По сумме чисел лет
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
              Дегрессивный метод с уменьшающейся долей списания каждый год
            </span>
          </div>
        </label>
        
        <label className={`
          flex items-start p-3 border rounded-lg cursor-pointer transition-colors
          ${formData.depreciationMethod === 'units_of_production' 
            ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-700' 
            : 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600'}
        `}>
          <input
            type="radio"
            name="depreciationMethod"
            value="units_of_production"
            checked={formData.depreciationMethod === 'units_of_production'}
            onChange={handleInputChange}
            className="form-radio mt-1"
          />
          <div className="ml-3">
            <span className="block text-sm font-medium text-gray-900 dark:text-white flex items-center">
              <Calculator className="w-4 h-4 mr-1.5 text-indigo-500" />
              Пропорционально объему продукции
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
              Списание в зависимости от фактического объема произведенной продукции
            </span>
          </div>
        </label>
      </div>

      {/* Дополнительные поля для метода "Пропорционально объему продукции" */}
      {formData.depreciationMethod === 'units_of_production' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
          <div>
            <label htmlFor="productionUnits" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Общее количество единиц продукции
            </label>
            <input
              type="text"
              id="productionUnits"
              name="productionUnits"
              value={formData.productionUnits}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.productionUnits ? 'input-error' : ''}`}
              placeholder="Введите количество единиц"
            />
            {errors.productionUnits && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.productionUnits}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="firstYearUnits" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Количество единиц в первый год
            </label>
            <input
              type="text"
              id="firstYearUnits"
              name="firstYearUnits"
              value={formData.firstYearUnits}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.firstYearUnits ? 'input-error' : ''}`}
              placeholder="Введите количество единиц"
            />
            {errors.firstYearUnits && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.firstYearUnits}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Специальный коэффициент */}
      <div className="mt-4">
        <label htmlFor="specialCoefficient" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Специальный коэффициент амортизации
        </label>
        <select
          id="specialCoefficient"
          name="specialCoefficient"
          value={formData.specialCoefficient}
          onChange={handleInputChange}
          className={`form-select w-full sm:w-1/3 ${errors.specialCoefficient ? 'input-error' : ''}`}
        >
          <option value="1">1 (стандартный)</option>
          <option value="2">2 (ускоренный)</option>
          <option value="3">3 (максимальный)</option>
        </select>
        {errors.specialCoefficient && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.specialCoefficient}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          В соответствии со ст. 259.3 НК РФ для определенных видов основных средств
        </p>
      </div>

      {/* Информационный блок по методам амортизации */}
      <div className="mt-4 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-100 dark:border-indigo-800">
        <div className="flex items-start">
          <Calculator className="w-4 h-4 text-indigo-500 mt-1 mr-2" />
          <div>
            <h4 className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
              О методах амортизации
            </h4>
            <ul className="list-disc list-inside text-xs text-indigo-600 dark:text-indigo-400 mt-1">
              <li><strong>Линейный метод</strong> — самый распространенный в России, обязателен для зданий и сооружений из 8-10 групп.</li>
              <li><strong>Метод уменьшаемого остатка</strong> — применяется для активов с быстрым моральным устареванием.</li>
              <li><strong>По сумме чисел лет</strong> — допускается в бухгалтерском учете, но не в налоговом.</li>
              <li><strong>Пропорционально объему</strong> — подходит для оборудования, износ которого напрямую зависит от выработки.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepreciationMethodSection;